import { useState, useEffect } from 'react';
import { auth } from '../lib/firebase';

/**
 * Hook to wait for Firebase Auth to be fully initialized with ID token
 * This prevents "Missing or insufficient permissions" errors due to race conditions
 * 
 * @param requireAuth - If true, only returns ready when user is authenticated
 * @returns boolean indicating if auth is ready
 */
export function useAuthReady(requireAuth: boolean = true): boolean {
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let mounted = true;
    
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!mounted) return;

      if (user) {
        try {
          // Wait for ID token to be available with claims
          const token = await user.getIdTokenResult();
          console.log('🔐 Auth ready - User:', token.claims.email || user.uid);
          
          // Small delay to ensure token is propagated to Firestore rules
          // This is critical for avoiding race conditions with Firestore security rules
          timeoutId = setTimeout(() => {
            if (mounted) {
              setAuthReady(true);
            }
          }, 500);
        } catch (error) {
          console.error('❌ Error getting ID token:', error);
          // Retry after a longer delay
          timeoutId = setTimeout(() => {
            if (mounted) {
              setAuthReady(true);
            }
          }, 1000);
        }
      } else {
        // If auth is not required, we're ready even without a user
        setAuthReady(!requireAuth);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [requireAuth]);

  return authReady;
}

